import React, { useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "providers/auth";
import { OriPersonaCard } from "components/ori";
import { useTranslation } from "lib/i18n/use-translation";
import { trackEvent } from "utility/mixpanel";
import { getOnboardingData, setOnboardingData } from "./use-onboarding-state";
import { OnboardingLoader } from "./onboarding-loader";
import Logo from "Assets/images/logo.svg";
import ChevronIcon from "Assets/icon/icon-chevron.svg";
import LockIcon from "Assets/icon/icon-lock-black.svg";
import CalendarImage from "Assets/images/Calendar.png";
import styles from "../ori-calendar.module.scss";

export const CalendarInvites = () => {
  const { learner, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["onboard"]);

  // Web shows both the invitation and the value-prop paragraph in one card
  // (mobile splits them across a card + a bottom sheet), so compose from the
  // two existing shared keys rather than duplicating the copy.
  const oriMessage = `${t("onboard:calendar.oriMessage")}\n\n${t(
    "onboard:calendar.sheet.body",
  )}`;

  const calendarData = learner?.onboarding?.inputs.find(
    (item) => item.id === "calendarPermission",
  );

  const stored = getOnboardingData();

  useEffect(() => {
    trackEvent("Onboarding Calendar Viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stored?.firstName === undefined) {
    return <Navigate to="/user/onboard" />;
  }

  const reverseLogic = Boolean((calendarData as any)?.input?.reverseLogic);

  const writePermission = (permission: boolean | null) => {
    setOnboardingData({
      calendarPermission:
        permission === null ? null : reverseLogic ? !permission : permission,
    });
  };

  const handleConnect = () => {
    trackEvent("Onboarding Calendar Connect Tapped");
    writePermission(true);
    navigate("/user/onboard/success");
  };

  const handleSkip = () => {
    trackEvent("Onboarding Calendar Skipped");
    writePermission(null);
    navigate("/user/onboard/success");
  };

  const handleBack = () => {
    trackEvent("Onboarding Calendar Back Tapped");
    navigate(-1);
  };

  if (loading || !calendarData) {
    return <OnboardingLoader />;
  }

  return (
    <div
      className={styles["screen"]}
      data-testid="onboarding-calendar-invites"
    >
      <div className={styles["header"]}>
        <button
          type="button"
          className={styles["backButton"]}
          onClick={handleBack}
          aria-label={t("onboard:consentLayout.goBack")}
        >
          <img src={ChevronIcon} alt="" />
        </button>
        <div className={styles["logoWrapper"]}>
          <img src={Logo} alt="Wone" className={styles["logo"]} />
        </div>
        <div />
      </div>

      <div className={styles["body"]}>
        <div className={styles["leftPanel"]}>
          <div className={styles["oriCardWrap"]}>
            <OriPersonaCard
              message={oriMessage}
              messageFooter={
                <div className={styles["privacyRow"]}>
                  <img
                    src={LockIcon}
                    alt=""
                    className={styles["privacyIcon"]}
                  />
                  <p className={styles["privacyText"]}>
                    {t("onboard:calendar.sheet.privacy")}
                  </p>
                </div>
              }
            />
          </div>
          <div className={styles["ctaStack"]}>
            <button
              type="button"
              className={styles["primaryButton"]}
              onClick={handleConnect}
              data-testid="onboarding-calendar-invites-connect"
            >
              {t("onboard:calendar.primary")}
            </button>
            <button
              type="button"
              className={styles["skipButton"]}
              onClick={handleSkip}
              data-testid="onboarding-calendar-invites-skip"
            >
              {t("onboard:consentLayout.skip")}
            </button>
          </div>
        </div>
        <div className={styles["rightPanel"]}>
          <img
            src={CalendarImage}
            alt=""
            className={styles["mediaImage"]}
          />
        </div>
      </div>
    </div>
  );
};
